import React, { Fragment, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import ToggleBox from 'components/toggler-components/ToggleBox';
import ToggleBoxHeader from 'components/toggler-components/ToggleBoxHeader';
import ToggleBoxBody from 'components/toggler-components/ToggleBoxBody';
import { cloneDeep } from 'lodash';
import { useTranslation } from 'react-i18next';
import tablePropertiesIcon from 'assets/images/icons/table-properties.svg';
import {
  defaultMediaProperties,
  fieldTypeAllowedProperties,
  formElementsDefaultProperties,
} from './utils';

import SingleFieldProperties from './SingleFieldProperties';
import { updateSavedChanges } from 'config/config';
import { removeSingleTriggerType } from './actions';

function FormProperties({
  selectedField,
  setSelectedField,
  formProperties,
  setFormProperties,
  toggleList,
  isNestedOption,
  dataSourcesList,
  companyFieldList,
  companyFieldMap,
  companyFieldDisplayValues,
  ticketTypeList,
}) {
  const { t } = useTranslation();

  const [elementPropertiesType, setElementPropertiesType] = useState(toggleList);
  const [selectedElementIndex, setSelectedElementIndex] = useState(-1);

  useEffect(() => {
    let list = toggleList;
    if (selectedField && selectedField.elementType) {
      list = toggleList.filter((singleTab) => {
        if (singleTab.excludedFor.indexOf(selectedField.elementType) === -1) return true;
        else return false;
      });
      setSelectedElementIndex(selectedField.position);
    } else {
      setSelectedElementIndex(-1);
    }
    setElementPropertiesType(list);
  }, [selectedField]);

  const rightToggleBoxOpen = (index) => {
    const elementsList = cloneDeep(elementPropertiesType);
    elementsList[index].isOpen = !elementsList[index].isOpen;
    setElementPropertiesType(elementsList);
  };
  const onChangeProperties = (key, value, type, extraValues) => {
    updateSavedChanges(true, 'FORM_DESIGN');
    // console.log({ key, value, type });
    let isResponseSetSelected = false;

    let formElementProperties = cloneDeep(formProperties);

    let elementAddedIn =
      selectedField.type === 'user-field' ? 'page' + selectedField.currentPage : 'supervisorFields';

    let elementProperties =
      formElementProperties[elementAddedIn].elements[selectedField.position].properties;
    if (selectedField.elementType === 'dateTime' && selectedField.type === 'user-field') {
      if (elementProperties.validationProperties['minRange']) {
        delete elementProperties.validationProperties['minRange'];
        delete elementProperties.validationProperties['maxRange'];
      }
    } else if (key === 'required' && value) {
      elementProperties[type]['readOnly'] = false;
      elementProperties[type]['hidden'] = false;
      elementProperties[type]['hideOnPdf'] = false;
    } else if (key === 'hidden' && value) {
      elementProperties[type]['required'] = false;
      elementProperties[type]['readOnly'] = false;
      elementProperties[type]['hideOnPdf'] = false;
      elementProperties[type]['restrictToSubmit'] = false;
    } else if (key === 'dataSource' && elementProperties[type].dataSource !== value) {
      elementProperties[type].displayColumn = '';
      elementProperties[type].valueColumn = '';
      elementProperties[type].bindingList = [];
    } else if (key === 'dataSourceType') {
      const elementType = value === 'internal' ? 'dataSource' : 'externalApi';
      setSelectedField((prev) => ({ ...prev, elementType }));
      formElementProperties[elementAddedIn].elements[selectedField.position].elementType =
        elementType;
      const baseProps = cloneDeep(
        value === 'internal'
          ? formElementsDefaultProperties.dataSource.basicProperties
          : formElementsDefaultProperties.externalApi.basicProperties,
      );
      const modifiedBaseProps = {
        ...baseProps,
        fieldTitle: elementProperties.basicProperties.fieldTitle,
        fieldHintText: elementProperties.basicProperties.fieldHintText,
        fieldPlaceholder: elementProperties.basicProperties.fieldPlaceholder,
        dataType: elementProperties.basicProperties.dataType,
        dataSourceType: elementProperties.basicProperties.dataSourceType,
      };
      elementProperties.basicProperties = { ...modifiedBaseProps };
    } else if (key === 'triggerType') {
      if (value !== 'onchange_other') {
        elementProperties[type].onChangeTriggerFieldId = '';
      }
      if (!['onload', 'onchange_other'].includes(value)) {
        elementProperties[type].selfFillColumnKey = '';
      }
    } else if (key === 'onChangeTriggerFieldId') {
      const currentValue = elementProperties[type][key];
      // If tries to set trigger field id to itself then set previous value
      if (
        formElementProperties[elementAddedIn].elements[selectedField.position].elementGlobalId ===
        value
      )
        value = currentValue;
    } else if (key === 'bindingList') {
      let selectedElement = formElementProperties[elementAddedIn].elements[selectedField.position];
      let formBindingObj = {
        ...formElementProperties.otherDetails.bindingElementsObj,
        [value.fieldId]: {
          fieldName: elementProperties[type].fieldTitle,
          fieldId: selectedElement.elementGlobalId
            ? selectedElement.elementGlobalId
            : selectedElement.uniqueIdToReplace,
        },
      };
      value = [...elementProperties[type].bindingList, value];
      formElementProperties.otherDetails.bindingElementsObj = formBindingObj;
    } else if (key === 'fieldTitle' && selectedField.type === 'user-field') {
      let selectedElement = formElementProperties[elementAddedIn].elements[selectedField.position];
      let updatedElementId = selectedElement.elementGlobalId
        ? selectedElement.elementGlobalId
        : selectedElement.uniqueIdToReplace;
      if (formElementProperties.otherDetails.allFormElementsObj[updatedElementId])
        formElementProperties.otherDetails.allFormElementsObj = {
          ...formElementProperties.otherDetails.allFormElementsObj,
          [updatedElementId]: {
            fieldName: value ? value : 'UnTitled',
            fieldId: updatedElementId,
          },
        };
      if (formElementProperties.otherDetails.onChangeTriggerElementsObj[updatedElementId])
        formElementProperties.otherDetails.onChangeTriggerElementsObj = {
          ...formElementProperties.otherDetails.onChangeTriggerElementsObj,
          [updatedElementId]: {
            fieldName: value ? value : 'UnTitled',
            fieldId: updatedElementId,
          },
        };
      // Below code is for checking page title uniqueness when typing
      // } else if (key === 'pageTitle') {
      //   const selectedElement =
      //     formElementProperties[elementAddedIn].elements[selectedField.position];
      //   const updatedElementId = selectedElement.elementGlobalId ?? selectedElement.uniqueIdToReplace;
      //   const currentPageTitle = selectedElement.properties.basicProperties.pageTitle ?? '';
      //   const pageElementsTitleList = [];
      //   for (const [key, formPageElementProperties] of Object.entries(formElementProperties)) {
      //     if (key.includes('page')) {
      //       const foundPageElement = formPageElementProperties.elements.find(
      //         (ele) => ele.elementType === 'page',
      //       );
      //       const foundPageId =
      //         foundPageElement.elementGlobalId ?? foundPageElement.triggerFlowReferenceId;
      //       if (foundPageElement && updatedElementId !== foundPageId) {
      //         const title = foundPageElement.properties.basicProperties?.pageTitle ?? '';
      //         if (title !== '') pageElementsTitleList.push(title);
      //       }
      //     }
      //   }

      //   if (pageElementsTitleList.includes(value)) value = currentPageTitle;
    } else if (key === 'removeBindingItem') {
      key = 'bindingList';
      const removedIndex = value.index;
      value = elementProperties[type][key];
      value.splice(removedIndex, 1);
      delete formElementProperties.otherDetails.bindingElementsObj[value.removedFieldId];
    } else if (key === 'requiredPictures' && value) {
      elementProperties[type].minPictures =
        parseInt(elementProperties[type].minPictures) > 1 ? elementProperties[type].minPictures : 1;
    } else if (
      [
        'minRange',
        'maxRange',
        'videoMinutes',
        'videoSeconds',
        'minPictures',
        'maxPictures',
      ].includes(key)
    ) {
      value = Number(value);
      if (key === 'videoSeconds' && value == 0 && elementProperties[type].videoMinutes === 0)
        value = 1;
      if (key === 'minPictures' && value > elementProperties[type].maxPictures) value = value - 1;
      if (key === 'maxPictures' && value < elementProperties[type].minPictures) value = value + 1;
      if (key === 'minRange' && value > elementProperties[type].maxRange) value = value - 1;
      if (key === 'maxRange' && value < elementProperties[type].minRange) value = value + 1;
    } else if (
      (key === 'allowPictures' || key === 'allowVideos' || key === 'enableWatermark') &&
      !value
    ) {
      // reset watermark properties when value of enableWatermark checkbox or if both("allowPictures" and "allowVideos") are false
      let resetWatermarkProperties = false;
      if (key === 'enableWatermark') {
        resetWatermarkProperties = true;
      } else {
        const otherKey = key === 'allowPictures' ? 'allowVideos' : 'allowPictures';
        if (elementProperties[type][otherKey] === false) {
          elementProperties[type]['enableWatermark'] = false;
          resetWatermarkProperties = true;
        }
      }
      if (resetWatermarkProperties) {
        elementProperties[type]['watermarkText'] = defaultMediaProperties.watermarkText;
        elementProperties[type]['watermarkAlignment'] = defaultMediaProperties.watermarkAlignment;
        elementProperties[type]['watermarkTextCenter'] = defaultMediaProperties.watermarkTextCenter;
        elementProperties[type]['watermarkUsedFieldsMap'] =
          defaultMediaProperties.watermarkUsedFieldsMap;
      }
    } else if (key === 'optionList') {
      if (Array.isArray(value) && value.length === 0) {
        elementProperties[type]['setName'] = undefined;
      }
      if (!Array.isArray(value) && value.setName) {
        elementProperties[type]['setName'] = value.setName;
        value = value.optionList;
        isResponseSetSelected = true;
      }
    } else if (key === 'geofence' && !value) {
      elementProperties[type]['geofenceRange'] = 5;
      elementProperties[type]['geofenceRestricted'] = false;
      elementProperties[type]['geofenceEnableDirection'] = false;
    } else if (key === 'companyFieldId') {
      elementProperties[type]['companyFieldDisplayColumns'] = extraValues.companyFieldColumn;
      elementProperties[type]['bindingList'] = [];
    }

    let previousOptionList = [];
    // store previous option list
    if (key === 'optionList') {
      previousOptionList = elementProperties[type][key];
      // console.log('abc2', previousOptionList);
    }
    elementProperties[type][key] = value;
    formElementProperties[elementAddedIn].elements[selectedField.position].properties =
      elementProperties;

    const currentPageElement =
      formElementProperties[elementAddedIn].elements.find((e) => e.elementType === 'page') ?? null;
    const currentPageId =
      currentPageElement?.elementGlobalId ?? currentPageElement?.triggerFlowReferenceId ?? null;

    if (key === 'optionList') {
      checkAndDeleteTriggersIfNeeded(
        previousOptionList,
        value,
        formElementProperties[elementAddedIn].elements,
        selectedField.position,
        currentPageId,
        formElementProperties.otherDetails.gotoPageTriggerMapObj,
        isResponseSetSelected,
      );
    }
    setFormProperties(formElementProperties);
  };

  // commented when trigger optionList changed bug fixed
  const removeAllTriggers = (elementsList, elementIndex, currentPageId, gotoPageTriggerMapObj) => {
    //   setSelectedField(null);
    const elementTriggers = elementsList[elementIndex].triggers;
    if (elementTriggers?.length) {
      for (let triggerIndex = elementTriggers.length - 1; triggerIndex >= 0; triggerIndex--) {
        const triggerTypes = elementTriggers[triggerIndex].types;
        for (let typeIndex = triggerTypes.length - 1; typeIndex >= 0; typeIndex--) {
          removeSingleTriggerType(
            elementsList,
            elementIndex,
            triggerIndex,
            typeIndex,
            currentPageId,
            gotoPageTriggerMapObj,
          );
        }
        elementTriggers.splice(triggerIndex, 1);
      }
      if (elementTriggers.length === 0) elementsList[elementIndex].triggers = null;
    }
  };

  const removeTriggersRelatedToChangedOption = (
    elementsList,
    elementIndex,
    currentPageId,
    gotoPageTriggerMapObj,
    previousValueOfUpdatedOption,
  ) => {
    const elementTriggers = elementsList[elementIndex].triggers;
    if (elementTriggers?.length) {
      for (let triggerIndex = elementTriggers.length - 1; triggerIndex >= 0; triggerIndex--) {
        const matchingValue = elementTriggers[triggerIndex].matchingValue;
        if (matchingValue === previousValueOfUpdatedOption) {
          // then remove all related existing triggers
          const triggerTypes = elementTriggers[triggerIndex].types;
          for (let typeIndex = triggerTypes.length - 1; typeIndex >= 0; typeIndex--) {
            removeSingleTriggerType(
              elementsList,
              elementIndex,
              triggerIndex,
              typeIndex,
              currentPageId,
              gotoPageTriggerMapObj,
            );
          }
          elementTriggers.splice(triggerIndex, 1);
        }
      }
      if (elementTriggers.length === 0) elementsList[elementIndex].triggers = null;
    }
  };

  const isOptionsChanged = (previousOptionList, newOptionList) => {
    const fieldOptionValues = previousOptionList.map((o) => o.value);
    const currentOptionValues = newOptionList.map((o) => o.value);
    // when new option added
    if (newOptionList.length > previousOptionList.length || !previousOptionList.length) {
      return null;
    } else {
      // when deleting option or modifying existing options value
      const previousValueOfUpdatedOption = fieldOptionValues.find(
        (v) => !currentOptionValues.includes(v),
      );
      return previousValueOfUpdatedOption;
    }
  };

  const checkAndDeleteTriggersIfNeeded = (
    previousOptionList,
    newOptionList,
    elementsList,
    elementIndex,
    currentPageId,
    gotoPageTriggerMapObj,
    isResponseSetSelected,
  ) => {
    const isOptionsCleared = newOptionList.length === 0;
    if (isOptionsCleared || isResponseSetSelected) {
      removeAllTriggers(elementsList, elementIndex, currentPageId, gotoPageTriggerMapObj);
    } else {
      const previousValueOfUpdatedOption = isOptionsChanged(previousOptionList, newOptionList);
      if (previousValueOfUpdatedOption) {
        removeTriggersRelatedToChangedOption(
          elementsList,
          elementIndex,
          currentPageId,
          gotoPageTriggerMapObj,
          previousValueOfUpdatedOption,
        );
      }
    }
  };

  const checkMultiChoiceDisabled = (key) => {
    return selectedField.elementType === 'checkList' &&
      key === 'multipleChoice' &&
      formProperties['page' + selectedField.currentPage].elements[selectedField.position].triggers
        ?.length
      ? true
      : false;
  };

  const checkGeofenceToggleEnabled = (key) => {
    let geofenceEnable = false;
    let anyLocationElementGeofenceFound = false;
    if (selectedField.elementType === 'location' && key === 'geoCoordinates') {
      const selectedFormElement =
        formProperties['page' + selectedField.currentPage].elements[selectedField.position];
      const selectedFormFieldId = selectedFormElement?.elementGlobalId
        ? selectedFormElement['elementGlobalId']
        : selectedFormElement['uniqueIdToReplace'];

      if (selectedFormFieldId) {
        const bindingObject = formProperties?.otherDetails?.bindingElementsObj;

        if (bindingObject[selectedFormFieldId]) {
          const datasourceFormFieldId = bindingObject[selectedFormFieldId].fieldId;

          const formPropertiesKeys = Object.keys(formProperties);

          for (let i = 0; i < formPropertiesKeys.length; i++) {
            const key = formPropertiesKeys[i];

            if (key.startsWith('page')) {
              const dataSourceElement = formProperties[key].elements.find((element) => {
                const datasourceId = element['elementGlobalId']
                  ? element['elementGlobalId']
                  : element['uniqueIdToReplace'];
                return datasourceId === datasourceFormFieldId;
              });

              if (dataSourceElement) {
                const dataSourceId =
                  dataSourceElement?.properties?.basicProperties?.dataSource ?? null;

                const dataSourceColumnId =
                  dataSourceElement?.properties?.basicProperties?.bindingList.find(
                    (bindingObj) => bindingObj.fieldId === selectedFormFieldId,
                  )?.columnId;

                if (dataSourceColumnId) {
                  if (dataSourceElement.elementType === 'externalApi') {
                    geofenceEnable = true;
                    break;
                  }
                  if (dataSourceElement.elementType === 'companyField') {
                    const bindedDataSource =
                      companyFieldMap[dataSourceElement.properties.basicProperties.companyFieldId];
                    if (bindedDataSource) {
                      geofenceEnable = bindedDataSource.fieldOptions.some(
                        (column) =>
                          column.value === dataSourceColumnId &&
                          column.columnType === 'geoCoordinates',
                      );
                    }
                  }
                  if (dataSourceId) {
                    const bindedDataSource = dataSourcesList.find(
                      (datasource) => datasource.value === dataSourceId,
                    );

                    if (bindedDataSource) {
                      geofenceEnable = bindedDataSource.columns.some(
                        (column) =>
                          column.value === dataSourceColumnId && column.type === 'geoCoordinates',
                      );
                    }
                  }
                }
                break;
              }
            }
          }
        }
      }
      if (geofenceEnable) {
        const formPropertiesKeys = Object.keys(formProperties);
        const allLocationElements = [];
        for (let i = 0; i < formPropertiesKeys.length; i++) {
          const key = formPropertiesKeys[i];

          if (key.startsWith('page')) {
            const locationElements = formProperties[key].elements.filter(
              (element) =>
                element.elementType === 'location' &&
                element[
                  selectedFormElement['elementGlobalId']
                    ? 'elementGlobalId'
                    : selectedFormElement['uniqueIdToReplace'] && 'uniqueIdToReplace'
                ] !== selectedFormFieldId,
            );

            allLocationElements.push(...locationElements);
          }
        }
        if (allLocationElements.length) {
          anyLocationElementGeofenceFound = allLocationElements.some(
            (element) => element.properties.validationProperties.geofence === true,
          );
        }
      }
    }
    return { geofenceEnable, anyLocationElementGeofenceFound };
  };

  return (
    <>
      {(selectedField && selectedElementIndex === selectedField.position) ||
      selectedElementIndex === -1 ? (
        <>
          {!isNestedOption && (
            <div className='section-header-area'>
              <div className='d-flex gap-2 align-items-center'>
                <img src={tablePropertiesIcon} />
                <p className='theme-text-black-1 theme-size-1_2 mb-0'>
                  {t('form_edit_properties')}
                </p>
              </div>
              <p className='text-center mb-0 theme-size-0_9'>
                {t('form_choose_the_options_as_you_like')}
              </p>
            </div>
          )}
          {elementPropertiesType.map((toggleBox, index) => {
            let propertiesList = selectedField
              ? selectedField.type === 'user-field'
                ? formProperties['page' + selectedField.currentPage].elements[
                    selectedField.position
                  ].properties
                : formProperties.supervisorFields.elements[selectedField.position].properties
              : {};
            return (
              <ToggleBox key={index}>
                <ToggleBoxHeader
                  show={toggleBox.isOpen}
                  toggleBoxOpen={() => rightToggleBoxOpen(index)}
                  title={toggleBox.title}
                />
                <ToggleBoxBody
                  show={toggleBox.isOpen}
                  classes={`${
                    !isNestedOption ? 'p-3 pt-0 d-flex flex-column gap-2' : 'd-flex flex-wrap gap-3'
                  }`}
                >
                  <Fragment>
                    {selectedField ? (
                      toggleBox.nestedOption ? (
                        <FormProperties
                          selectedField={selectedField}
                          setSelectedField={setSelectedField}
                          formProperties={formProperties}
                          setFormProperties={setFormProperties}
                          toggleList={toggleBox.nestedOption}
                          isNestedOption={true}
                          dataSourcesList={dataSourcesList}
                          companyFieldList={companyFieldList}
                          companyFieldMap={companyFieldMap}
                          companyFieldDisplayValues={companyFieldDisplayValues}
                          ticketTypeList={ticketTypeList}
                        />
                      ) : (
                        fieldTypeAllowedProperties[toggleBox.defaultPropsKey].map(
                          (singleProperty, index) => {
                            if (
                              (singleProperty.isAllowedForAll &&
                                singleProperty.restrictedFieldTypes.indexOf(
                                  selectedField.elementType,
                                ) === -1) ||
                              singleProperty.allowedFieldTypes.indexOf(selectedField.elementType) >
                                -1
                            ) {
                              let fieldSectionProperties =
                                propertiesList[toggleBox.defaultPropsKey + 'Properties'];
                              return (
                                fieldSectionProperties && (
                                  <SingleFieldProperties
                                    key={index}
                                    selectedField={selectedField}
                                    onChangeProperties={onChangeProperties}
                                    singleProperty={singleProperty}
                                    fieldSectionProperties={fieldSectionProperties}
                                    toggleBox={toggleBox}
                                    propertiesList={propertiesList}
                                    otherDetails={formProperties.otherDetails}
                                    dataSourcesList={
                                      selectedField.elementType === 'dataSource'
                                        ? dataSourcesList
                                        : []
                                    }
                                    companyFieldList={
                                      selectedField.elementType === 'companyField'
                                        ? companyFieldList
                                        : []
                                    }
                                    selectedCompanyFieldIds={
                                      selectedField.elementType === 'companyField'
                                        ? formProperties.otherDetails.selectedCompanyFields
                                        : []
                                    }
                                    companyFieldMap={companyFieldMap}
                                    companyFieldDisplayValues={companyFieldDisplayValues}
                                    ticketTypeList={ticketTypeList}
                                    hasTriggers={checkMultiChoiceDisabled(singleProperty.key)}
                                    geofenceEnabledObject={checkGeofenceToggleEnabled(
                                      singleProperty.key,
                                    )}
                                  />
                                )
                              );
                            }
                          },
                        )
                      )
                    ) : (
                      ''
                    )}
                  </Fragment>
                </ToggleBoxBody>
              </ToggleBox>
            );
          })}
        </>
      ) : (
        <></>
      )}
    </>
  );
}
FormProperties.propTypes = {
  selectedField: PropTypes.object,
  setSelectedField: PropTypes.func.isRequired,
  formProperties: PropTypes.object.isRequired,
  setFormProperties: PropTypes.func.isRequired,
  toggleList: PropTypes.array.isRequired,
  dataSourcesList: PropTypes.array.isRequired,
  companyFieldList: PropTypes.array.isRequired,
  companyFieldMap: PropTypes.object,
  companyFieldDisplayValues: PropTypes.object,
  ticketTypeList: PropTypes.array.isRequired,
  isNestedOption: PropTypes.bool.isRequired,
};
export default FormProperties;
